import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../../requestOptions';
import type { ExpansionAssignmentPlan } from './types';

const validate = (runId: number, subscriptionId: number, productIds?: number[]) => {
  if (!runId || runId < 1) {
    throw new Error('Valid run ID is required');
  }
  if (!subscriptionId || subscriptionId < 1) {
    throw new Error('Valid subscription ID is required');
  }
  // `min:1` on the API, the same as `assign` — an absent key means "every ready product", so an
  // empty array can only be a client bug, and the dialog must not ask a question the button is
  // then refused for.
  if (productIds !== undefined && productIds.length === 0) {
    throw new Error('Select at least one product to assign');
  }
};

type RequestParams = {
  runId: number;
  /**
   * Any subscription of the organization — the API authorises the caller against it and scopes
   * every read by organization, never by this id.
   */
  subscriptionId: number;
  productIds?: number[];
};

/**
 * What Assign would write, for the confirm dialog. The counts come from the API rather than from
 * the table's own rows because only the server knows which parents a selection drags in.
 */
export const getAssignmentPlan = async (
  scope: RequestScope,
  { runId, subscriptionId, productIds }: RequestParams,
  options: ReadRequestOptions
): Promise<ChannelApiResponse<ExpansionAssignmentPlan>> => {
  try {
    validate(runId, subscriptionId, productIds);

    const result = await scopedChannelApi(scope).get<ExpansionAssignmentPlan>(`atts/expansion/runs/${runId}/assignment-plan`, {
      ...options,
      headers: {
        'x-astur-subscription-id': subscriptionId.toString(),
      },
      params: {
        product_ids: productIds,
      },
    });

    return {
      data: result.data,
      status: result.status,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
